/**
 * Copyright ©2024 Drivepoint
 */

import {Mutex} from "async-mutex";
import {Utilities} from "@bainbridge-growth/node-common";
import {EventBus, State} from "@bainbridge-growth/node-frontend";
import NotFoundError from "../../errors/NotFoundError.ts";
import {ModelSettingsProps} from "../../types/ExcelModel.ts";
import ExcelService from "./ExcelService.ts";

export default class ModelSettings {

  private static _mutex = new Mutex();
  private static _value: ModelSettingsProps = undefined;
  private static _timestamp: number = 0;

  static async start(): Promise<void> {
    EventBus.registerMany("WorksheetChanged", "WorksheetNameChanged", ModelSettings.onEvent);
  }

  static onEvent(event: any): void {
    if ((event.nameAfter === "Settings" || event.nameBefore === "Settings") && ModelSettings._timestamp) {
      ModelSettings.clear();
    }
  }

  static clear(): void {
    logger.trace("clearing cached settings");
    ModelSettings._timestamp = 0;
    ModelSettings.get().then(data => { State.set("modelSettings", data); });
  }

  private static async _getPlanSettings(context: Excel.RequestContext) {
    try {
      const worksheet = context.workbook.worksheets.getItemOrNullObject("Plan Settings");
      await context.sync();
      if (worksheet.isNullObject) { return {}; }
      const range = worksheet.getUsedRange(true);
      range.load("values");
      await context.sync();
      return range.values.reduce((newObj, el) => {
        newObj = {...newObj, [el[0].split(".")[1]]: el[2]};
        return newObj;
      }, {});
    } catch (error: any) {
      // Surface as a soft warning rather than a system:error toast — the agent
      // should remain usable on workbooks where Plan Settings is absent or
      // malformed. ModelHealth renders a "Limited" state via plan.id.
      logger.warn("[ModelSettings] Plan Settings unreadable; continuing without plan settings", error?.message);
      return {};
    }
  }

  static findHeaderColumnIndices(rows: any[][]): [number, number] {
    const headerRow = rows.find(row => row.includes("id") && row.includes("Value"));
    const idIndex = headerRow?.indexOf("id") ?? 1;
    const valueIndex = headerRow?.indexOf("Value") ?? 3;
    return [idIndex, valueIndex];
  }

  static buildSettings(rows: any[][], idIndex: number, valueIndex: number): Record<string, any> {
    const settings: Record<string, any> = {};

    for (const row of rows) {
      const [name, value] = [row[idIndex], row[valueIndex]];
      if (name?.startsWith("settings.")) {
        const normalizedName = name.replace("settings.", "");
        const normalizedValue = typeof value === "string" ? value.trim() : value;
        settings[normalizedName[0].toLowerCase() + normalizedName.slice(1)] = normalizedValue;
      }
    }
    return settings;
  
  }

  static async get(): Promise<ModelSettingsProps | undefined> {
    return ModelSettings._mutex.runExclusive(async () => {
      // Check if we already have cached settings
      if (!Utilities.isEmpty(ModelSettings._value) && Date.now() - ModelSettings._timestamp < 60000) {
        logger.trace("using cached settings");
        return ModelSettings._value;
      }
      // Check if Excel is available
      if (!ExcelService.isExcelAvailable()) {
        logger.warn("[ModelSettings] Excel API not available, returning default settings");
        // Return a default set of settings when Excel is not available
        ModelSettings._value = {};
        ModelSettings._timestamp = Date.now();
        return ModelSettings._value;
      }
      logger.trace("rebuilding settings...");
      ModelSettings._value = {};
      ModelSettings._timestamp = Date.now();
      let mode: any = "";
      try {
        // Get current calculation mode
        mode = await ExcelService.getCalculationMode();
        return await Excel.run(async (context: Excel.RequestContext) => {
          await ExcelService.setCalculationMode(Excel.CalculationMode.manual);
          const worksheet = context.workbook.worksheets.getItemOrNullObject("Settings");
          await context.sync();
          if (worksheet.isNullObject) { throw new NotFoundError(); }
          const range = worksheet.getUsedRange(true);
          range.load("values");
          await context.sync();
          const planSettings = await this._getPlanSettings(context);
          const [idIndex, valueIndex] = ModelSettings.findHeaderColumnIndices(range.values);
          const settings = ModelSettings.buildSettings(range.values, idIndex, valueIndex);
          
          ModelSettings._value = {...settings, ...planSettings};
          logger.trace("caching settings");
          return {...settings, ...planSettings};
        });
      } catch (error: any) {
        if (error.code === "ItemNotFound" || error.code === 404) {
          // Missing Settings tab is the canonical "this isn't a SmartModel workbook"
          // signal. Don't toast it as a system error — PlanService will mark the
          // plan as not_smartmodel and ModelHealth will render the warning state.
          logger.warn("[ModelSettings] Settings tab not found; treating workbook as non-SmartModel", error?.message);
          return undefined;
        }
        throw error;
      } finally {
        // Restore calculation mode if Excel is available
        if (ExcelService.isExcelAvailable() && typeof mode !== "undefined") {
          await ExcelService.setCalculationMode(mode);
        }
      }
    });
  }

}
